// Captation audio d'une réunion : micro seul (présentiel) ou micro + son d'un onglet (visio),
// mélangés dans un seul flux enregistré par tranches. Aucune dépendance à React.

export const CHUNK_MS = 10000;
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];

export const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported?.(type)) || null;
};

export const recordingSupport = () => ({
  micro: Boolean(navigator.mediaDevices?.getUserMedia && pickMimeType()),
  visio: Boolean(navigator.mediaDevices?.getDisplayMedia && pickMimeType()),
});

// En salle, on garde les voix lointaines (pas de réduction de bruit) ; en visio, on évite que
// le haut-parleur repasse dans le micro.
const microConstraints = (source) => ({
  audio: source === 'visio'
    ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    : { echoCancellation: false, noiseSuppression: false, autoGainControl: true },
});

export class MeetingCapture {
  constructor({ source, onChunk, onLevel, onTabAudioEnded }) {
    this.source = source;
    this.onChunk = onChunk;
    this.onLevel = onLevel;
    this.onTabAudioEnded = onTabAudioEnded;
    this.streams = [];
    this.index = 0;
  }

  // Ouvre micro et partage d'onglet (demandes d'autorisation) sans encore enregistrer.
  async open() {
    this.mimeType = pickMimeType();
    if (!this.mimeType) throw new Error("Ce navigateur ne sait pas enregistrer l'audio.");

    let display = null;
    if (this.source === 'visio') {
      // Chrome/Edge exigent une vidéo pour partager un onglet ; seule la piste audio est enregistrée.
      display = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: { suppressLocalAudioPlayback: false },
        preferCurrentTab: false,
        selfBrowserSurface: 'exclude',
        systemAudio: 'include',
      });
      this.streams.push(display);
      if (!display.getAudioTracks().length) {
        this.release();
        throw new Error("Aucun son partagé : choisis l'onglet de la visio et coche « Partager l'audio de l'onglet ».");
      }
      display.getVideoTracks().forEach((track) => { track.enabled = false; });
      display.getAudioTracks()[0].addEventListener('ended', () => this.onTabAudioEnded?.());
    }

    const micro = await navigator.mediaDevices.getUserMedia(microConstraints(this.source)).catch((error) => {
      this.release();
      throw new Error(error?.name === 'NotAllowedError'
        ? "Accès au micro refusé : autorise-le dans la barre d'adresse du navigateur."
        : "Impossible d'ouvrir le micro.");
    });
    this.streams.push(micro);

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.context = new AudioContextClass();
    const destination = this.context.createMediaStreamDestination();
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 512;
    [micro, display].filter((stream) => stream?.getAudioTracks().length).forEach((stream) => {
      const node = this.context.createMediaStreamSource(new MediaStream(stream.getAudioTracks()));
      node.connect(destination);
      node.connect(this.analyser);
    });
    this.mixed = destination.stream;
    this.meter();
  }

  record() {
    this.recorder = new MediaRecorder(this.mixed, { mimeType: this.mimeType, audioBitsPerSecond: 48000 });
    this.recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) this.onChunk(this.index++, event.data);
    };
    this.stopped = new Promise((resolve) => { this.recorder.onstop = resolve; });
    this.recorder.start(CHUNK_MS);
    this.startedAt = Date.now();
  }

  meter() {
    const samples = new Uint8Array(this.analyser.fftSize);
    const tick = () => {
      if (!this.analyser) return;
      this.analyser.getByteTimeDomainData(samples);
      let peak = 0;
      for (let i = 0; i < samples.length; i += 1) peak = Math.max(peak, Math.abs(samples[i] - 128));
      this.onLevel?.(Math.min(1, peak / 64));
      this.frame = requestAnimationFrame(tick);
    };
    tick();
  }

  get elapsedSeconds() {
    return this.startedAt ? (Date.now() - this.startedAt) / 1000 : 0;
  }

  // Termine l'enregistrement ; renvoie le nombre de tranches produites.
  async stop() {
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.stop();
      await this.stopped;
    }
    this.release();
    return this.index;
  }

  release() {
    cancelAnimationFrame(this.frame);
    this.analyser = null;
    this.streams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    this.streams = [];
    this.context?.close().catch(() => {});
    this.context = null;
  }
}
